import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { PlusCircle, Trash2, FileDown, MapPin, Calendar, Zap, TrendingUp, Sparkles, Loader2, AlertTriangle, GitCompare, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Project } from '../types';
import { fetchProjects, deleteProject as deleteProjectAPI } from '../utils/api';
import { exportProject } from '../utils/storage';
import { generatePDFReport } from '../utils/pdfExport';
import { formatCurrency, formatDate, formatEnergy, formatCoordinates } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { SEO, SEO_CONFIGS } from '../components/SEO';
import { CompareModal } from '../components/CompareModal';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const PROJECTS_PER_PAGE = 9; // 3x3 grid

export function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedForCompare, setSelectedForCompare] = useState<string[]>([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { t } = useTranslation();
  const { credits } = useAuth();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProjects();
      setProjects(data);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteProjectAPI(id);
      await loadProjects();
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Failed to delete project. Please try again.');
    }
  };

  const handleExport = (project: Project) => {
    exportProject(project);
  };

  const handlePDFExport = (project: Project) => {
    generatePDFReport(project);
  };

  const handleToggleCompare = (id: string) => {
    if (selectedForCompare.includes(id)) {
      setSelectedForCompare(selectedForCompare.filter((pid) => pid !== id));
    } else {
      if (selectedForCompare.length < 3) {
        setSelectedForCompare([...selectedForCompare, id]);
      } else {
        toast.warning('⚠️ Maximum 3 projects', {
          description: 'You can compare up to 3 projects at a time. Deselect a project to add another.',
          duration: 4000,
        });
      }
    }
  };

  const handleOpenCompareModal = () => {
    if (selectedForCompare.length >= 2) {
      setIsCompareModalOpen(true);
    } else {
      toast.error(t('dashboard.compareLimit', 'You can only compare up to 2 projects at a time.'));
    }
  };

  const handleCloseCompareModal = () => {
    setIsCompareModalOpen(false);
  };

  if (loading) {
    return <LoadingSpinner message={t('dashboard.loading', 'Loading Projects')} />;
  }

  if (error) {
    return (
      <>
        <SEO {...SEO_CONFIGS.dashboard} />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-20"
        >
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            {t('dashboard.error', 'Error Loading Projects')}
          </h2>
          <p className="text-slate-600 mb-6">
            {error}
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => loadProjects()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-semibold rounded-xl shadow-lg"
          >
            {t('dashboard.retry', 'Retry')}
          </motion.button>
        </motion.div>
      </>
    );
  }

  if (projects.length === 0) {
    return (
      <>
        <SEO {...SEO_CONFIGS.dashboard} />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-20"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="relative w-32 h-32 mx-auto mb-8"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#4F819F] to-[#4E7F9D] rounded-3xl flex items-center justify-center shadow-2xl shadow-[#4F819F]/30">
              <Zap className="w-16 h-16 text-white" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-[#4F819F] to-[#4E7F9D] rounded-3xl blur-2xl opacity-40 animate-pulse"></div>
          </motion.div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4 bg-gradient-to-r from-[#4F819F] to-[#456070] bg-clip-text text-transparent">
            {t('dashboard.noProjects')}
          </h2>
          <p className="text-slate-600 mb-10 max-w-md mx-auto text-lg">
            {t('dashboard.noProjectsDesc')}
          </p>
          <Link to="/new">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-semibold rounded-xl shadow-xl shadow-[#4F819F]/30 hover:shadow-2xl hover:shadow-[#4F819F]/40 transition-all"
              onClick={() => {
                // Clear any saved form data when starting a new analysis
                localStorage.removeItem('newAnalysisFormData');
              }}
            >
              <PlusCircle className="w-6 h-6" />
              {t('dashboard.createFirst')}
            </motion.div>
          </Link>
        </motion.div>
      </>
    );
  }

  const totalPages = Math.ceil(projects.length / PROJECTS_PER_PAGE);
  const currentProjects = projects.slice((currentPage - 1) * PROJECTS_PER_PAGE, currentPage * PROJECTS_PER_PAGE);

  return (
    <div>
      <SEO {...SEO_CONFIGS.dashboard} />
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-10"
      >
        <div>
          <h2 className="text-5xl font-bold text-slate-900 mb-3 tracking-tight">
            {t('dashboard.title')}
          </h2>
          <p className="text-slate-600 text-lg flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            {t('dashboard.subtitle')}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-3">
          {selectedForCompare.length >= 2 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleOpenCompareModal}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-[#4F819F] text-[#4F819F] shadow-lg hover:bg-[#4F819F] hover:text-white cursor-pointer rounded-xl font-semibold transition-all"
            >
              <GitCompare className="w-5 h-5" />
              Compare Selected ({selectedForCompare.length})
            </motion.button>
          )}
          
          <Link to="/new">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-semibold rounded-xl shadow-xl shadow-[#4F819F]/30 hover:shadow-2xl hover:shadow-[#4F819F]/40 transition-all"
              onClick={() => {
                // Clear any saved form data when starting a new analysis
                localStorage.removeItem('newAnalysisFormData');
              }}
            >
              <PlusCircle className="w-5 h-5" />
              {t('nav.newAnalysis')}
            </motion.div>
          </Link>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentProjects.map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            onDelete={handleDelete}
            onExport={handleExport}
            onPDFExport={handlePDFExport}
            index={index}
            onSelectForCompare={handleToggleCompare}
            isSelectedForCompare={selectedForCompare.includes(project.id)}
          />
        ))}
      </div>

      <CompareModal
        isOpen={isCompareModalOpen}
        onClose={handleCloseCompareModal}
        projects={projects.filter((p) => selectedForCompare.includes(p.id) && p.results)}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-10">
          <motion.button
            whileHover={currentPage > 1 ? { scale: 1.05 } : {}}
            whileTap={currentPage > 1 ? { scale: 0.95 } : {}}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-lg transition-all ${
              currentPage === 1
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white hover:shadow-xl'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </motion.button>
          
          <span className="text-slate-700 font-medium">
            Page {currentPage} of {totalPages}
          </span>
          
          <motion.button
            whileHover={currentPage < totalPages ? { scale: 1.05 } : {}}
            whileTap={currentPage < totalPages ? { scale: 0.95 } : {}}
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-lg transition-all ${
              currentPage === totalPages
                ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white hover:shadow-xl'
            }`}
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>
      )}
    </div>
  );
}

interface ProjectCardProps {
  project: Project;
  onDelete: (id: string) => void;
  onExport: (project: Project) => void;
  onPDFExport: (project: Project) => void;
  index: number;
  onSelectForCompare: (id: string) => void;
  isSelectedForCompare: boolean;
}

function ProjectCard({ project, onDelete, onExport, onPDFExport, index, onSelectForCompare, isSelectedForCompare }: ProjectCardProps) {
  const hasResults = !!project.results;
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={{ y: -8 }}
      className="relative rounded-2xl backdrop-blur-xl bg-white/90 border border-slate-200 shadow-xl overflow-hidden hover:shadow-2xl transition-shadow"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#4F819F]/5 via-transparent to-blue-500/5 opacity-0 hover:opacity-100 transition-opacity"></div>
      
      {/* Header */}
      <div className="relative bg-gradient-to-br from-[#4F819F]/10 to-[#4E7F9D]/10 p-5 border-b border-slate-200">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-bold text-xl text-slate-900 truncate flex-1">{project.name}</h3>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#4F819F] to-[#4E7F9D] flex items-center justify-center shadow-lg">
            <Zap className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(project.createdAt)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-5 space-y-4 bg-white">
        <div className="flex items-start gap-3 text-sm">
          <MapPin className="w-4 h-4 text-slate-500 mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-slate-700 truncate">{formatCoordinates(project.location.latitude, project.location.longitude)}</p>
            {project.location.address && (
              <p className="text-slate-500 text-xs mt-1 truncate">{project.location.address}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">
          <Zap className="w-4 h-4 text-[#4F819F]" />
          <span className="text-slate-900 font-semibold">{project.systemConfig.capacity} kWp</span>
          <span className="text-slate-600">• {project.systemConfig.panelType}</span>
        </div>

        {hasResults && (
          <div className="pt-4 border-t border-slate-200 space-y-3">
            <div className="flex justify-between items-center p-3 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200">
              <span className="text-slate-700 text-sm">{t('projectDetails.annualProduction')}</span>
              <span className="font-bold text-blue-900">
                {formatEnergy(project.results.energyProduction.annualProduction)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
              <span className="text-slate-700 text-sm flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {t('projectDetails.npv')}
              </span>
              <span className="font-bold text-green-700">
                {formatCurrency(project.results.financialMetrics.npv, project.financialParams.currency)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-lg bg-gradient-to-r from-[#4F819F]/10 to-[#4E7F9D]/10 border border-[#4F819F]/30">
              <span className="text-slate-700 text-sm">{t('projectDetails.paybackPeriod')}</span>
              <span className="font-bold text-[#41474A]">
                {project.results.financialMetrics.paybackPeriod.toFixed(1)} {t('projectDetails.years')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="relative p-4 bg-slate-50 border-t border-slate-200 flex gap-2">
        <Link to={`/project/${project.id}`} className="flex-1">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] text-white font-semibold rounded-lg shadow-lg shadow-[#4F819F]/30 hover:shadow-xl hover:shadow-[#4F819F]/40 transition-all"
          >
            {t('dashboard.viewProject')}
          </motion.button>
        </Link>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onPDFExport(project)}
          className="px-3 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-all group relative"
        >
          <FileDown className="w-5 h-5" />
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
            Download PDF Report
          </span>
        </motion.button>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="px-3 py-2.5 bg-red-50 border border-red-300 text-red-600 rounded-lg hover:bg-red-100 transition-all group relative"
            >
              <Trash2 className="w-5 h-5" />
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                Delete Project
              </span>
            </motion.button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-slate-900">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                {t('dashboard.confirmDelete')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600">
                {t('dashboard.deleteWarning', `Are you sure you want to delete "${project.name}"? This action cannot be undone.`)}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t('common.cancel', 'Cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => onDelete(project.id)}
              >
                {t('common.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onSelectForCompare(project.id)}
          disabled={!hasResults}
          className={`px-3 py-2.5 rounded-lg transition-all group relative ${
            isSelectedForCompare 
              ? 'bg-[#4F819F] text-white' 
              : hasResults 
                ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50' 
                : 'bg-slate-200 border border-slate-300 text-slate-400 cursor-not-allowed'
          }`}
        >
          {isSelectedForCompare ? <Check className="w-5 h-5" /> : <GitCompare className="w-5 h-5" />}
          <span className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
            {hasResults 
              ? (isSelectedForCompare ? 'Remove from Comparison' : 'Add to Comparison') 
              : 'Complete Analysis Required'}
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
}